import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Calendar, Users, MapPin, ArrowRight, ChevronLeft, ChevronRight, Clock } from 'lucide-react';
import EventRegistrationModal from './EventRegistrationModal';
import PaymentSuccessModal from './PaymentSuccessModal';
import { useLanguage } from '../context/LanguageContext';
import { useTranslate } from '../hooks/useTranslate';
import PhotoGallery from './PhotoGallery';
import EventImageCarousel from './EventImageCarousel';
import { OptimizedImage } from './ui/optimized-image';

// Composant pour le premier événement mis en avant
const FeaturedEvent = ({ event, nextEventTranslated, reservedMembersTranslated, learnMoreTranslated, setSelectedEvent, formatDate, formatTime, getEventColor, getEventIcon }) => {
  const { translatedText: eventTitleTranslated } = useTranslate(event.title);
  const { translatedText: eventDescTranslated } = useTranslate(event.description || "");
  
  // Utiliser cover_image_url en priorité, sinon image_url
  const coverImage = event.cover_image_url || event.image_url;
  
  return (
    <div className="mb-12">
      <h3 
        className="text-xl font-semibold mb-6"
        style={{ 
          fontFamily: '"Lora", serif',
          color: 'rgb(196, 150, 96)'
        }}
      >
        {nextEventTranslated}
      </h3>
      <Card className="bg-white border-gray-200">
        <CardContent className="p-8">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="flex items-center mb-4">
                <div className={`p-2 rounded-full ${getEventColor(event.event_type)} mr-3`}>
                  {getEventIcon(event.event_type)}
                </div>
                <span 
                  className="text-lg font-semibold"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    color: 'rgb(196, 150, 96)'
                  }}
                >
                  {formatDate(event.start_datetime)}
                </span>
                {event.member_only && (
                  <span className="ml-3 px-3 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                    {reservedMembersTranslated}
                  </span>
                )}
                {typeof event.places_remaining === 'number' && (
                  <span className={`ml-3 px-3 py-1 text-xs font-semibold rounded-full ${
                    event.places_remaining === 0 
                      ? 'bg-red-100 text-red-800' 
                      : event.places_remaining <= 10 
                        ? 'bg-orange-100 text-orange-800' 
                        : 'bg-green-100 text-green-800'
                  }`}>
                    {event.places_remaining === 0 
                      ? 'Complet' 
                      : `${event.places_remaining} place${event.places_remaining > 1 ? 's' : ''}`}
                  </span>
                )}
              </div>
              <h4 
                className="text-2xl font-bold mb-4"
                style={{ 
                  fontFamily: '"Lora", serif',
                  color: 'rgb(196, 150, 96)'
                }}
              >
                {eventTitleTranslated}
              </h4>
              <p 
                className="text-gray-700 leading-relaxed mb-6"
                style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
                dangerouslySetInnerHTML={{ 
                  __html: (eventDescTranslated || event.description || "").substring(0, 200) + '...' 
                }}
              />
              <div className="space-y-2 mb-6">
                {event.location && (
                  <div className="flex items-center text-gray-600">
                    <MapPin className="w-4 h-4 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                    <span>{event.location}</span>
                  </div>
                )}
                <div className="flex items-center text-gray-600">
                  <Clock className="w-4 h-4 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                  <span>{formatTime(event.start_datetime)} - {formatTime(event.end_datetime)}</span>
                </div>
              </div>
              <Button 
                className="text-white hover:opacity-90"
                onClick={() => setSelectedEvent(event)}
                style={{ 
                  fontFamily: '"Lora", serif',
                  backgroundColor: 'rgb(196, 150, 96)'
                }}
              >
                {learnMoreTranslated}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            {coverImage && (
              <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden">
                <OptimizedImage 
                  src={coverImage} 
                  alt={event.title}
                  className="w-full h-full object-cover rounded-lg"
                  useThumbnail={false}
                  priority={true}
                />
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Composant pour une carte d'événement individuelle
const EventCard = ({ event, reservedMembersTranslated, seeDetailsTranslated, setSelectedEvent, formatDate, getEventColor, getEventIcon }) => {
  const { translatedText: eventTitleTranslated } = useTranslate(event.title);
  const { translatedText: eventDescTranslated } = useTranslate(event.description || "");
  const { currentLanguage } = useLanguage();
  
  // Utiliser cover_image_url en priorité, sinon image_url
  const coverImage = event.cover_image_url || event.image_url;
  
  return (
    <Card className="hover:shadow-lg transition-shadow duration-300">
      <CardContent className="p-6">
        <div className="flex items-start space-x-4">
          {coverImage && (
            <div className="w-32 h-24 md:w-40 md:h-28 bg-gray-100 rounded-lg overflow-hidden flex-shrink-0">
              <OptimizedImage 
                src={coverImage} 
                alt={event.title}
                className="w-full h-full object-cover"
                useThumbnail={true}
              />
            </div>
          )}
          
          <div className="flex-1">
            <div className="flex items-center mb-2">
              <div className={`p-1 rounded-full ${getEventColor(event.event_type)} mr-2`}>
                {getEventIcon(event.event_type)}
              </div>
              <span 
                className="text-sm font-medium text-gray-600"
                style={{ fontFamily: '"Lora", serif' }}
              >
                {formatDate(event.start_datetime)}
              </span>
              {event.member_only && (
                <span className="ml-2 px-2 py-1 bg-amber-100 text-amber-800 text-xs font-semibold rounded-full">
                  {reservedMembersTranslated}
                </span>
              )}
              {typeof event.places_remaining === 'number' && (
                <span className={`ml-2 px-2 py-1 text-xs font-semibold rounded-full ${
                  event.places_remaining === 0 
                    ? 'bg-red-100 text-red-800' 
                    : event.places_remaining <= 10 
                      ? 'bg-orange-100 text-orange-800' 
                      : 'bg-green-100 text-green-800'
                }`}>
                  {event.places_remaining === 0 
                    ? 'Complet' 
                    : `${event.places_remaining} place${event.places_remaining > 1 ? 's' : ''}`}
                </span>
              )}
            </div>
            
            <h4 
              className="text-xl font-semibold mb-3"
              style={{ 
                fontFamily: '"Lora", serif',
                color: 'rgb(196, 150, 96)'
              }}
            >
              {eventTitleTranslated}
            </h4>
            
            <p 
              className="text-gray-600 leading-relaxed mb-4"
              style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
              dangerouslySetInnerHTML={{ 
                __html: (eventDescTranslated || event.description || "").substring(0, 150) + '...' 
              }}
            />
            
            <Button 
              variant="outline" 
              size="sm" 
              className="hover:bg-opacity-10"
              onClick={() => setSelectedEvent(event)}
              style={{ 
                fontFamily: '"Lora", serif',
                borderColor: 'rgb(196, 150, 96)',
                color: 'rgb(196, 150, 96)'
              }}
            >
              {seeDetailsTranslated}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const EventsAgenda = () => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [pastEvents, setPastEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('upcoming'); // 'upcoming' ou 'archive'
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [eventToRegister, setEventToRegister] = useState(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successRegistration, setSuccessRegistration] = useState(null);
  const [successEvent, setSuccessEvent] = useState(null);
  const [verifyingPayment, setVerifyingPayment] = useState(false);
  const { currentLanguage } = useLanguage();

  // Traductions
  const { translatedText: agendaTitleTranslated } = useTranslate("L'agenda des lignages");
  const { translatedText: agendaDescTranslated } = useTranslate("Restez informés de nos prochains événements, assemblées générales et activités culturelles");
  const { translatedText: noEventsTranslated } = useTranslate("Aucun événement à venir pour le moment.");
  const { translatedText: nextEventTranslated } = useTranslate("Notre prochain événement");
  const { translatedText: reservedMembersTranslated } = useTranslate("🔒 Réservé aux membres");
  const { translatedText: seeDetailsTranslated } = useTranslate("Voir les détails");
  const { translatedText: registerTranslated } = useTranslate("S'inscrire");
  const { translatedText: availableSpotsTranslated } = useTranslate("places disponibles");
  const { translatedText: fullEventTranslated } = useTranslate("Complet");
  const { translatedText: upcomingEventsTranslated } = useTranslate("Événements à venir");
  const { translatedText: archiveEventsTranslated } = useTranslate("Archives");
  const { translatedText: noPastEventsTranslated } = useTranslate("Aucun événement passé.");
  const { translatedText: learnMoreTranslated } = useTranslate("En savoir plus");
  const { translatedText: eventDetailsTranslated } = useTranslate("Détails de l'événement");
  const { translatedText: closeTranslated } = useTranslate("Fermer");
  const { translatedText: tariffsTranslated } = useTranslate("Tarifs");
  const { translatedText: registerEventTranslated } = useTranslate("S'inscrire à cet événement");
  
  // Traductions pour le modal (titre et description de l'événement sélectionné)
  const { translatedText: modalEventTitleTranslated } = useTranslate(selectedEvent?.title || "");
  const { translatedText: modalEventDescTranslated } = useTranslate(selectedEvent?.description || "");

  useEffect(() => {
    fetchEvents();
    checkPaymentReturn();
  }, []);

  // Vérifier si on revient de Stripe
  const checkPaymentReturn = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get("session_id");
    const eventSuccess = urlParams.get("event_success");

    console.log('🔍 Vérification retour Stripe:', { sessionId, eventSuccess });

    if (sessionId && eventSuccess) {
      // Activer l'indicateur de vérification
      setVerifyingPayment(true);
      
      // Nettoyer les accolades si présentes
      const cleanSessionId = sessionId.replace(/[{}]/g, '');
      console.log('🔄 Session ID nettoyé:', cleanSessionId);
      
      // Polling pour vérifier le statut
      await pollPaymentStatus(cleanSessionId);
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10; // Plus de tentatives mais plus rapides
    
    if (attempts >= maxAttempts) {
      setVerifyingPayment(false); // Désactiver l'indicateur
      alert("Vérification du paiement en cours... Veuillez vérifier votre email pour la confirmation.");
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    try {
      console.log(`🔄 Tentative ${attempts + 1}/${maxAttempts}`);
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/registration/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          console.log('✅ Paiement confirmé ! Récupération des détails...');
          
          // Récupérer les détails de l'inscription
          const regResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/registration/${data.registration_id}`);
          if (regResponse.ok) {
            const registration = await regResponse.json();
            setSuccessRegistration(registration);
            
            // Récupérer l'événement correspondant
            const eventResponse = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events?upcoming_only=false`);
            if (eventResponse.ok) {
              const eventsData = await eventResponse.json();
              const evt = eventsData.events.find(e => e.id === registration.event_id);
              setSuccessEvent(evt);
            }
            
            setShowSuccessModal(true);
            setVerifyingPayment(false); // Désactiver l'indicateur
          }
          
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }
      }
      
      // Continuer le polling avec délai réduit à 500ms
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 500);
    } catch (error) {
      console.error('Erreur vérification paiement:', error);
      // Réessayer même en cas d'erreur
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 500);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      // Récupérer tous les événements
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events?upcoming_only=false`);
      if (response.ok) {
        const data = await response.json();
        const allEvents = data.events || [];
        
        // Séparer les événements à venir et passés
        const now = new Date();
        const upcoming = allEvents.filter(event => new Date(event.end_datetime) >= now);
        const past = allEvents.filter(event => new Date(event.end_datetime) < now);
        
        // Trier par date
        upcoming.sort((a, b) => new Date(a.start_datetime) - new Date(b.start_datetime));
        past.sort((a, b) => new Date(b.start_datetime) - new Date(a.start_datetime)); // Plus récents en premier
        
        setUpcomingEvents(upcoming);
        setPastEvents(past);
      }
    } catch (error) {
      console.error('Erreur chargement événements:', error);
    } finally {
      setLoading(false);
    }
  };


  // Formater la date SANS conversion de timezone
  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Extraire la date directement du string ISO
    const match = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, year, month, day] = match;
      const months = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
      return `${parseInt(day)} ${months[parseInt(month) - 1]} ${year}`;
    }
    return '';
  };

  // Formater l'heure SANS conversion de timezone
  const formatTime = (dateString) => {
    if (!dateString) return '';
    // Extraire l'heure directement du string ISO
    const match = dateString.match(/T(\d{2}):(\d{2})/);
    if (match) {
      const [, hours, minutes] = match;
      return `${hours}:${minutes}`;
    }
    return '';
  };

  const getEventIcon = (type) => {
    switch (type) {
      case 'assembly': return <Users className="w-5 h-5" />;
      case 'community': return <Users className="w-5 h-5" />;
      case 'cultural': return <MapPin className="w-5 h-5" />;
      default: return <Calendar className="w-5 h-5" />;
    }
  };

  const getEventColor = (type) => {
    switch (type) {
      case 'assembly': return 'bg-blue-600 text-white';
      case 'community': return 'bg-green-600 text-white';
      case 'cultural': return 'bg-purple-600 text-white';
      default: return 'bg-amber-600 text-white';
    }
  };

  if (loading) {
    return (
      <section id="event" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2" 
                 style={{ borderBottomColor: 'rgb(196, 150, 96)' }}></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="event" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Indicateur de vérification du paiement */}
        {verifyingPayment && (
          <div className="mb-8 max-w-2xl mx-auto">
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6 text-center">
              <div className="flex items-center justify-center mb-3">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
              <h3 className="text-lg font-semibold text-blue-900 mb-2">
                Vérification de votre paiement en cours...
              </h3>
              <p className="text-sm text-blue-700">
                Merci de patienter quelques instants. Votre confirmation va apparaître.
              </p>
            </div>
          </div>
        )}

        <div className="text-center mb-12">
          <h2 
            className="text-3xl font-bold mb-4"
            style={{ 
              fontFamily: '"Lora", serif',
              color: 'rgb(196, 150, 96)'
            }}
          >
            {agendaTitleTranslated}
          </h2>
          <p 
            className="text-gray-600 max-w-2xl mx-auto"
            style={{ fontFamily: '"Lora", serif', fontSize: '17px' }}
          >
            {agendaDescTranslated}
          </p>
        </div>

        {/* Onglets */}
        <div className="flex justify-center mb-8 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('upcoming')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'upcoming'
                ? 'border-b-2 text-amber-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === 'upcoming' ? { borderBottomColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' } : {}}
          >
            {upcomingEventsTranslated}
          </button>
          <button
            onClick={() => setActiveTab('archive')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'archive'
                ? 'border-b-2 text-amber-700'
                : 'text-gray-500 hover:text-gray-700'
            }`}
            style={activeTab === 'archive' ? { borderBottomColor: 'rgb(196, 150, 96)', color: 'rgb(196, 150, 96)' } : {}}
          >
            {archiveEventsTranslated}
          </button>
        </div>

        {activeTab === 'upcoming' ? (
          upcomingEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
                {noEventsTranslated}
              </p>
            </div>
          ) : (
            <>
              {/* Next Event Highlight */}
              {upcomingEvents[0] && (
                <FeaturedEvent 
                  event={upcomingEvents[0]}
                  nextEventTranslated={nextEventTranslated}
                  reservedMembersTranslated={reservedMembersTranslated}
                  learnMoreTranslated={learnMoreTranslated}
                  setSelectedEvent={setSelectedEvent}
                  formatDate={formatDate}
                  formatTime={formatTime}
                  getEventColor={getEventColor}
                  getEventIcon={getEventIcon}
                />
              )}

              {/* Tous les autres événements à venir */}
              {upcomingEvents.length > 1 && (
                <div>
                  <h3 
                    className="text-xl font-semibold mb-6"
                    style={{ 
                      fontFamily: '"Lora", serif',
                      color: 'rgb(196, 150, 96)'
                    }}
                  >
                    Autres événements à venir
                  </h3>
                  
                  <div className="grid gap-6">
                    {upcomingEvents.slice(1).map((event) => (
                      <EventCard 
                        key={event.id}
                        event={event}
                        reservedMembersTranslated={reservedMembersTranslated}
                        seeDetailsTranslated={seeDetailsTranslated}
                        setSelectedEvent={setSelectedEvent}
                        formatDate={formatDate}
                        getEventColor={getEventColor}
                        getEventIcon={getEventIcon}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          )
        ) : (
          // Onglet Archive
          pastEvents.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500" style={{ fontFamily: '"Lora", serif' }}>
                {noPastEventsTranslated}
              </p>
            </div>
          ) : (
            <div className="grid gap-6">
              {pastEvents.map((event) => (
                <EventCard 
                  key={event.id}
                  event={event}
                  reservedMembersTranslated={reservedMembersTranslated}
                  seeDetailsTranslated={seeDetailsTranslated}
                  setSelectedEvent={setSelectedEvent}
                  formatDate={formatDate}
                  getEventColor={getEventColor}
                  getEventIcon={getEventIcon}
                />
              ))}
            </div>
          )
        )}

        {/* Modal pour afficher les détails d'un événement */}
        {selectedEvent && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedEvent(null)}
          >
            <div 
              className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-8">
                {/* Header avec bouton fermer */}
                <div className="flex justify-between items-start mb-6">
                  <div className="flex-1 pr-8">
                    <h3 
                      className="text-3xl font-bold mb-2"
                      style={{ 
                        fontFamily: '"Lora", serif',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      {currentLanguage === 'fr' ? selectedEvent.title : (modalEventTitleTranslated || selectedEvent.title)}
                    </h3>
                    {!selectedEvent.is_public && (
                      <span className="px-3 py-1 bg-amber-100 text-amber-800 text-sm font-semibold rounded-full">
                        {reservedMembersTranslated}
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => setSelectedEvent(null)}
                    className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
                  >
                    ×
                  </button>
                </div>

                {/* Image de l'événement / Carrousel si album photo */}
                <EventImageCarousel 
                  coverImage={selectedEvent.cover_image_url || selectedEvent.image_url}
                  photoAlbum={selectedEvent.photo_album}
                  title={selectedEvent.title}
                />

                {/* Informations de l'événement */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                    <span style={{ fontFamily: '"Lora", serif' }}>
                      {formatDate(selectedEvent.start_datetime)}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-gray-600">
                    <Clock className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                    <span style={{ fontFamily: '"Lora", serif' }}>
                      {formatTime(selectedEvent.start_datetime)} - {formatTime(selectedEvent.end_datetime)}
                    </span>
                  </div>
                  
                  {selectedEvent.location && (
                    <div className="flex items-center text-gray-600">
                      <MapPin className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                      <span style={{ fontFamily: '"Lora", serif' }}>
                        {selectedEvent.location}
                      </span>
                    </div>
                  )}
                  
                  {selectedEvent.max_attendees && (
                    <div className="flex items-center text-gray-600">
                      <Users className="w-5 h-5 mr-2" style={{ color: 'rgb(196, 150, 96)' }} />
                      <span style={{ fontFamily: '"Lora", serif' }}>
                        {selectedEvent.max_attendees} {availableSpotsTranslated}
                      </span>
                    </div>
                  )}
                </div>

                {/* Prix / Tarifs */}
                {selectedEvent.tariffs && selectedEvent.tariffs.length > 0 ? (
                  <div className="mb-6">
                    <h4 className="font-semibold mb-2" style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}>
                      {tariffsTranslated}
                    </h4>
                    <div className="space-y-2">
                      {selectedEvent.tariffs.map((tariff, idx) => (
                        <div key={idx} className="flex justify-between">
                          <span style={{ fontFamily: '"Lora", serif' }}>{tariff.label}</span>
                          <span 
                            className="font-bold"
                            style={{ fontFamily: '"Lora", serif', color: 'rgb(196, 150, 96)' }}
                          >
                            {tariff.price.toFixed(2)} €
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : selectedEvent.price > 0 && (
                  <div className="mb-6">
                    <div 
                      className="text-2xl font-bold"
                      style={{ 
                        fontFamily: '"Lora", serif',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      {selectedEvent.price.toFixed(2)} €
                    </div>
                  </div>
                )}

                {/* Description complète */}
                <div 
                  className="prose prose-lg max-w-none mb-6"
                  style={{ 
                    fontFamily: '"Lora", serif',
                    fontSize: '17px',
                    lineHeight: '1.8'
                  }}
                  dangerouslySetInnerHTML={{ 
                    __html: currentLanguage === 'fr' ? selectedEvent.description : (modalEventDescTranslated || selectedEvent.description)
                  }}
                />

                {/* Bouton d'inscription - N'afficher que si l'événement a des tarifs configurés */}
                {((selectedEvent.tariffs && selectedEvent.tariffs.length > 0) || (selectedEvent.price !== undefined && selectedEvent.price !== null)) && (
                  <div className="flex gap-4">
                    {selectedEvent.member_only ? (
                      <Button
                        className="flex-1 text-white"
                        onClick={() => {
                          window.location.href = '/membre';
                        }}
                        style={{ 
                          fontFamily: '"Lora", serif',
                          backgroundColor: 'rgb(196, 150, 96)'
                        }}
                      >
                        🔒 Connectez-vous à l'espace membre
                      </Button>
                    ) : (
                      <Button
                        className="flex-1 text-white"
                        onClick={() => {
                          setEventToRegister(selectedEvent);
                          setShowRegistrationModal(true);
                          setSelectedEvent(null);
                        }}
                        style={{ 
                          fontFamily: '"Lora", serif',
                          backgroundColor: 'rgb(196, 150, 96)'
                        }}
                      >
                        {registerEventTranslated}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      onClick={() => setSelectedEvent(null)}
                      style={{ 
                        fontFamily: '"Lora", serif',
                        borderColor: 'rgb(196, 150, 96)',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      {closeTranslated}
                    </Button>
                  </div>
                )}

                {/* Bouton Fermer seul si pas de tarifs */}
                {!((selectedEvent.tariffs && selectedEvent.tariffs.length > 0) || (selectedEvent.price !== undefined && selectedEvent.price !== null)) && (
                  <div className="flex justify-end">
                    <Button
                      variant="outline"
                      onClick={() => setSelectedEvent(null)}
                      style={{ 
                        fontFamily: '"Lora", serif',
                        borderColor: 'rgb(196, 150, 96)',
                        color: 'rgb(196, 150, 96)'
                      }}
                    >
                      {closeTranslated}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modal d'inscription */}
        {showRegistrationModal && eventToRegister && (
          <EventRegistrationModal
            event={eventToRegister}
            isPublic={eventToRegister.is_public}
            onClose={() => {
              setShowRegistrationModal(false);
              setEventToRegister(null);
            }}
          />
        )}

        {/* Modal de succès */}
        {showSuccessModal && successRegistration && (
          <PaymentSuccessModal
            registration={successRegistration}
            event={successEvent}
            onClose={() => {
              setShowSuccessModal(false);
              window.location.reload();
            }}
          />
        )}
      </div>
    </section>
  );
};

export default EventsAgenda;